import { cabinBaggage, checkInBaggage, meal, secondBaggagePerTraveler } from '@eti/products';

import { AgeType } from '@eti/schema-types';
import type {
  AddOnExtraProductType,
  AddOnOrdersType,
  AncillaryBundleContentItemType,
  AncillaryBundlesType,
  BuySpecificationType,
  ExtraProductsType,
  ExtraProductType,
  TravelerChoicePricesType,
  TravelerSeatMapPriceType,
  TravelerType,
} from '../../../models';

export type IncludedProductType = {
  buySpecification: BuySpecificationType;
  description: string;
  name: string;
  id: string;
  code: string;
};

export type TravelerProductPreference = {
  travelerName?: string | null;
  preference: string;
};

const SERVICE_BAGGAGE_GDS_ID = 235;

const getSelectedBundleProducts = (
  bundleContent: AncillaryBundleContentItemType[],
): IncludedProductType[] => {
  return bundleContent.reduce<IncludedProductType[]>(
    (acc: IncludedProductType[], contentItem: AncillaryBundleContentItemType) => {
      if (contentItem) {
        acc.push({
          description: contentItem.upsellTextWhenBooked ?? '',
          name: contentItem.name ?? '',
          id: contentItem.productTypeId,
          code: contentItem.code,
          buySpecification: null,
        });
      }
      return acc;
    },
    [],
  );
};

const getTravelerSeat = (
  traveler: TravelerType,
  t: (...args: any) => string,
  travelerSeatMapPrice?: TravelerSeatMapPriceType,
) => {
  if (traveler.ageType === AgeType.Inf) {
    return t('OrderDetails.WhatIsIncluded.Included.Seating.Infant.Text');
  }

  if (!travelerSeatMapPrice) {
    return '-';
  }

  return travelerSeatMapPrice.seat;
};

export const getSegmentTravelerSeatMap = (
  boundIndex: number,
  boundTravelerSeatMapPrices: TravelerSeatMapPriceType[],
  segmentIndex: number,
  travelers: TravelerType[],
  t: (...args: any) => string,
): TravelerProductPreference[] => {
  return travelers.map((traveler) => {
    const travelerSeatMapPrice = boundTravelerSeatMapPrices.find(
      (price) =>
        price?.boundIndex === boundIndex &&
        price.segmentIndex === segmentIndex &&
        price.travelerId === traveler.id,
    );

    return {
      travelerName: traveler.name,
      preference: getTravelerSeat(traveler, t, travelerSeatMapPrice),
    };
  });
};

export const getSegmentTravelerMeal = (
  boundTravelerChoicePrices: TravelerChoicePricesType,
  travelers: TravelerType[],
): TravelerProductPreference[] => {
  return travelers.map((traveler: TravelerType) => {
    const travelerMealPrice = boundTravelerChoicePrices.find(
      (price) => price?.travelerId === traveler.id,
    );

    return {
      travelerName: traveler.name,
      preference: travelerMealPrice?.choice ?? '-',
    };
  });
};

export const getIncludedProducts = (
  addOnOrders: AddOnOrdersType,
  ancillaryBundles: AncillaryBundlesType,
  extraProducts: ExtraProductsType,
): IncludedProductType[] => {
  const primaryAndAddOnProducts =
    addOnOrders.length > 0
      ? addOnOrders.reduce(
          (
            acc: (ExtraProductType | NonNullable<AddOnExtraProductType>)[],
            { extraProducts: addOnExtraProducts },
          ) =>
            addOnExtraProducts
              ? acc.concat(addOnExtraProducts.filter((product) => product !== null))
              : acc,
          extraProducts,
        )
      : extraProducts;

  const includedMealProduct = primaryAndAddOnProducts.find(
    (product) => Number(product.id) === Number(meal.id),
  );

  const excludedIds: number[] = [
    secondBaggagePerTraveler.id,
    cabinBaggage.id,
    checkInBaggage.id,
    meal.id,
    SERVICE_BAGGAGE_GDS_ID,
  ];

  const includedProductsWithoutBaggageAndMeal = primaryAndAddOnProducts.filter(
    (product) => !excludedIds.includes(Number(product.id)),
  );

  const selectedAncillaryBundleProducts: IncludedProductType[] = ancillaryBundles.reduce(
    (acc: IncludedProductType[], ancillaryBundle) => {
      const selectedOption = ancillaryBundle?.selectedOption;
      if (selectedOption !== null) {
        const selectedBundle = ancillaryBundle.options.find(
          (option) => option?.id === selectedOption,
        );
        if (selectedBundle && selectedBundle.content) {
          acc.push(...getSelectedBundleProducts(selectedBundle.content));
        }
      }
      return acc;
    },
    [],
  );

  const allIncludedProducts = [
    ...(includedMealProduct ? [includedMealProduct] : []),
    ...includedProductsWithoutBaggageAndMeal,
  ];

  const allIncludedProductsFormatted: IncludedProductType[] = allIncludedProducts.map(
    (includedProduct) => ({
      buySpecification: includedProduct.buySpecification,
      id: includedProduct.id,
      description: includedProduct.texts.upsellTextWhenBooked ?? '',
      name: includedProduct.name,
      code: includedProduct.code,
    }),
  );

  return [...allIncludedProductsFormatted, ...selectedAncillaryBundleProducts];
};
